import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useContext } from "react";
import { AuthProvider, AuthContext } from "./context/AuthContext";

import AdminAuditLogs from "./pages/AdminAuditLogs";
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ProtectedRoute from "./components/common/ProtectedRoute";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import CustomRequest from "./pages/CustomRequest";
import CustomRequestDetailPage from "./pages/CustomRequestDetailPage";
import MyRequests from "./pages/MyRequests";
import Profile from "./pages/Profile";
import ProfileForm from "./pages/ProfileForm";
import ChangePasswordForm from "./pages/ChangePasswordForm";
import FreelancerDashboard from "./pages/FreelancerDashboard";
import PendingApproval from "./pages/PendingApproval";
import AdminFreelancerApproval from "./pages/AdminFreelancerApproval";
import EmailVerification from "./pages/EmailVerification";
import VerifyEmailPending from "./pages/VerifyEmailPending";

import useNotifications from "./hooks/useNotifications";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { PayPalScriptProvider } from "@paypal/react-paypal-js";

/* ---------------- Route-level error boundary ---------------- */

const RouteErrorBoundary = ({ children }) => (
  <ErrorBoundary
    onError={(error) => {
      console.log("Route error captured:", error.message);
    }}
    onReset={() => window.scrollTo(0, 0)}
  >
    {children}
  </ErrorBoundary>
);

/* ---------------- Root App ---------------- */

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PayPalScriptProvider
          options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID }}
        >
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </PayPalScriptProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

/* ---------------- AppContent (SAFE to use AuthContext) ---------------- */

function AppContent() {
  const { user } = useContext(AuthContext);

  // 🔔 Global notifications (runs once per login session)
  useNotifications(user);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />

      <main className="flex-grow">
        <Routes>
          <Route
            path="/"
            element={
              <RouteErrorBoundary>
                <Home />
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/login"
            element={
              <RouteErrorBoundary>
                <Login />
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/register"
            element={
              <RouteErrorBoundary>
                <Register />
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/pending-approval"
            element={
              <RouteErrorBoundary>
                <PendingApproval />
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/verify-email/:token"
            element={
              <RouteErrorBoundary>
                <EmailVerification />
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/verify-email-pending"
            element={
              <RouteErrorBoundary>
                <VerifyEmailPending />
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/freelancer/dashboard"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute requiredRole="FREELANCER" requireApproval>
                  <FreelancerDashboard />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/admin/freelancer-approval"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminFreelancerApproval />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/admin/audit-logs"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminAuditLogs />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/custom-request"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <CustomRequest />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/request/:id"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <CustomRequestDetailPage />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/my-requests"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <MyRequests />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/project/:id"
            element={
              <RouteErrorBoundary>
                <ProjectDetailPage />
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/profile"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/profile/edit"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <ProfileForm />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />

          <Route
            path="/change-password"
            element={
              <RouteErrorBoundary>
                <ProtectedRoute>
                  <ChangePasswordForm />
                </ProtectedRoute>
              </RouteErrorBoundary>
            }
          />
        </Routes>
      </main>

      <Footer />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="colored"
        style={{ marginTop: "80px" }}
      />
    </div>
  );
}
